import cn from 'classnames';
import React, { useMemo, useState } from 'react';

import { CollectionFolder } from 'editor/src/store/collections/types';
import linkCollectionElementsOperation from 'editor/src/store/design/operation/linkCollectionElementsOperation';
import setCollectionAssetLabelOperation from 'editor/src/store/design/operation/setCollectionAssetLabelOperation';
import setCollectionDisplayOptionOperation from 'editor/src/store/design/operation/setCollectionDisplayOptionOperation';
import unlinkCollectionElementOperation from 'editor/src/store/design/operation/unlinkCollectionElementOperation';
import { MediaImage, StructureIndex } from 'editor/src/store/design/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import {
  AdvancedPersonalisationElementData,
  CollectionDisplayOption,
  getAdvPersoDataFromElement,
  getCollectionAssetLabel,
} from 'editor/src/store/plugins/utils/advPersoUtils';

import Accordion from 'editor/src/component/Accordion';
import TextInput from 'editor/src/component/TextInput';

import styles from './index.module.scss';

interface Props {
  image: MediaImage;
  address: StructureIndex;
  collectionInfo: AdvancedPersonalisationElementData;
  folder: CollectionFolder;
}

interface LinkOption {
  label: string;
  value: string;
}

function CollectionSettings({ image, address, collectionInfo, folder }: Props) {
  const dispatch = useDispatch();
  const designData = useSelector((state) => state.design.designData);
  const [isOptionsCollapsed, setOptionsCollapsed] = useState(true);

  const [pickedAssetId, setPickedAssetId] = useState<string>(
    () => (folder.assets.some((asset) => asset.id === image.imageId) ? image.imageId : folder.assets[0]?.id) ?? '',
  );

  const pickedAsset = useMemo(
    () => folder.assets.find((asset) => asset.id === pickedAssetId) ?? folder.assets[0],
    [folder.assets, pickedAssetId],
  );

  const linkOptions: LinkOption[] = useMemo(() => {
    if (!designData) {
      return [];
    }
    const options: LinkOption[] = [];
    designData.spreads.forEach((spread, spreadIndex) => {
      spread.pages.forEach((page, pageIndex) => {
        page.groups.media?.forEach((element, elementIndex) => {
          if (element.uuid === image.uuid) {
            return;
          }
          const otherData = getAdvPersoDataFromElement(element);
          if (!otherData) {
            return;
          }
          options.push({
            label: element.name,
            value: `${spreadIndex}:${pageIndex}:${elementIndex}`,
          });
        });
      });
    });
    return options;
  }, [designData, image.uuid]);

  function selectDisplayOption(displayOption: CollectionDisplayOption) {
    dispatch(setCollectionDisplayOptionOperation({ address, displayOption }));
  }

  function onLabelChange(assetId: string, assetName: string, value: string) {
    dispatch(setCollectionAssetLabelOperation(address, assetId, value, assetName));
  }

  function onLinkChange(value: string) {
    if (!value) {
      return;
    }
    const [spreadIndex, pageIndex, elementIndex] = value.split(':').map(Number);
    dispatch(linkCollectionElementsOperation(address, { spreadIndex, pageIndex, elementIndex }));
  }

  function unlink() {
    dispatch(unlinkCollectionElementOperation(address));
  }

  const linkSection = (
    <div className={styles.section}>
      {/* TODO: Lokalino */}
      <div className={styles.label}>Link</div>
      {collectionInfo.linkGroupId ? (
        <div className={styles.linkRow}>
          {/* TODO: Lokalino */}
          <span className={cn(styles.linkedText, 'cy-collection-linked')}>Linked</span>
          <button type="button" className={cn(styles.unlinkBtn, 'cy-collection-unlink')} onClick={unlink}>
            {/* TODO: Lokalino */}
            Unlink
          </button>
        </div>
      ) : (
        <>
          {/* TODO: Lokalino */}
          <div className={styles.label}>Link with…</div>
          <select
            className={cn(styles.linkSelect, 'cy-collection-link-select')}
            value=""
            onChange={(e) => onLinkChange(e.target.value)}
          >
            <option value="" disabled>
              {/* TODO: Lokalino */}
              Select a layer…
            </option>
            {linkOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );

  const optionsAccordion = (content: React.ReactNode) => (
    <Accordion
      // TODO: Lokalino
      header="Options"
      headerClassName={cn(styles.optionsHeader, 'cy-collection-options-accordion')}
      collapsed={isOptionsCollapsed}
      setCollapsed={setOptionsCollapsed}
    >
      {content}
    </Accordion>
  );

  return (
    <div className={cn(styles.CollectionSettings, 'cy-collection-settings')}>
      {optionsAccordion(
        <>
          <div className={styles.section}>
            {/* TODO: Lokalino */}
            <div className={styles.label}>Display</div>
            <div className={styles.displayOptions}>
              <button
                type="button"
                className={cn(styles.displayBtn, 'cy-collection-display-thumbnails', {
                  [styles.active]: collectionInfo.displayOption === CollectionDisplayOption.Thumbnail,
                })}
                onClick={() => selectDisplayOption(CollectionDisplayOption.Thumbnail)}
              >
                {/* TODO: Lokalino */}
                Thumbnails
              </button>
              <button
                type="button"
                className={cn(styles.displayBtn, 'cy-collection-display-dropdown', {
                  [styles.active]: collectionInfo.displayOption === CollectionDisplayOption.Dropdown,
                })}
                onClick={() => selectDisplayOption(CollectionDisplayOption.Dropdown)}
              >
                {/* TODO: Lokalino */}
                Dropdown
              </button>
            </div>
          </div>

          {collectionInfo.displayOption === CollectionDisplayOption.Dropdown && pickedAsset && (
            <div className={styles.section}>
              {/* TODO: Lokalino */}
              <div className={styles.label}>Option labels</div>
              <select
                className={cn(styles.labelPicker, 'cy-collection-label-picker')}
                value={pickedAsset.id}
                onChange={(e) => setPickedAssetId(e.target.value)}
              >
                {folder.assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {getCollectionAssetLabel(collectionInfo, asset)}
                  </option>
                ))}
              </select>
              <TextInput
                key={pickedAsset.id}
                className={cn(styles.labelInput, 'cy-collection-label-input')}
                value={collectionInfo.assetLabels?.[pickedAsset.id] ?? ''}
                placeholder={pickedAsset.name}
                onChange={(value) => onLabelChange(pickedAsset.id, pickedAsset.name, value)}
              />
            </div>
          )}

          {linkSection}
        </>,
      )}
    </div>
  );
}

export default React.memo(CollectionSettings);
